<template>
  <v-card :id="$cy.newClientModal">
    <v-card-title class="pb-2">
      <v-window :value="step">
        <v-window-item :value="steps.FORM">
          <v-row>
            <v-col cols="12" class="py-0">
              <span class="headline-2">{{ I18NGetter().useCreateClient.ADD_CLIENT }}</span>
            </v-col>
            <v-col class="pb-0 pt-3" cols="12" v-if="!hideClientTypeTabs">
              <fp-chip-radio
                item-key="type"
                item-name="name_pl"
                :local-storage-key="LAST_CLIENT_TYPE"
                :id="$cy.createClientType"
                v-model="client.isCompany"
                :items="tabsHeaders" />
            </v-col>
          </v-row>
        </v-window-item>
        <v-window-item :value="steps.VERIFICATION">
          <client-badge
            :readonly="true"
            :client="client"/>
        </v-window-item>
      </v-window>
    </v-card-title>
    <v-divider />
    <v-card-text>
      <create-client-windows-form
        :step="step"
        :ref="CREATE_CLIENTS_WINDOW_REF"
        @closeModal="closeModal()"
        :client="client"
        :scan-access-end-date="scanAccessEndDate"
        :deal-stage="dealStage">
        <template v-slot:customErrorAction="{clientId}">
          <a @click="onExistingClient(clientId)" class="text-decoration-underline">{{ I18NGetter().useCreateClient.PROCEED_TO_CLIENT }}</a>
        </template>
      </create-client-windows-form>
    </v-card-text>
    <v-divider/>
    <v-card-actions>
      <v-window :value="step">
        <v-window-item :value="steps.FORM">
          <v-row class="align-center" no-gutters>
            <v-col class="flex-nowrap shrink">
              <fp-btn
                primary
                :id="$cy.createClintSubmitForm"
                :click="() => onFormSubmit()"
                :class="$onboarding.tours.LEAD.step.FILL_AGREEMENTS">
                {{isSMS ? I18NGetter().useCreateClient.SEND_SMS : I18NGetter().useCreateClient.ATTACH_SCANS}}
              </fp-btn>
            </v-col>
            <v-col class="body-2" v-if="isSMS && client.phone">
              {{ I18NGetter().useCreateClient.SEND_SMS_TO_NUMBER }}: {{client.phone}}
            </v-col>
          </v-row>
        </v-window-item>
        <v-window-item :value="steps.VERIFICATION">
          <loading-button
            class="mr-4"
            @click="step--">
            <v-icon>mdi-chevron-left</v-icon>
          </loading-button>
          <loading-button
            primary
            :disabled="addClientButtonDisabled"
            :click="() => addClient()"
            :class="$onboarding.tours.LEAD.step.SMS"
            >{{ I18NGetter().useCreateClient.ADD_CLIENT }}</loading-button>
        </v-window-item>
      </v-window>
      <v-spacer/>
      <v-btn class="shrink" :class="client.phone ? 'mb-6' : ''" @click="closeModal()" outlined :id="$cy.closeCreateClientModal">{{ I18NGetter().useCreateClient.CANCEL }}</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script lang="ts">
import {Component} from 'vue-property-decorator';
import {VueModalComponent} from 'fp-components';
import AddClientSmsApi from '@/modules/client/services/ClientSmsApi';
import Client from '../../../../models/Client';
import {ModalPayload} from '@/types/typings';
import {ModalAction} from '@/types/typings/enums';
import ClientApi from '@/modules/client/services/ClientApi';
import EditClientForm from '@/modules/deals/views/edit-deal/EditClientForm.vue';
import NavigationTabs from '@/components/commons/NavigationTabs.vue';
import {IMultiformIconDictionaryElement} from '@/modules/multiForm/shared/IMultiformDictionary';
import AddClientScans from '@/modules/client/views/create-client/AddClientScans.vue';
import {AgreementType} from '@/modules/client/views/client-agreements/Agreements';
import {CreateClientFormStep} from '@/modules/client/enums/CreateClientFormStep';
import CreateClientWindowsForm from '@/modules/client/views/create-client/CreateClientWindowsForm.vue';
import {ICreateClientWindowsValidation} from '@/modules/client/models/CreateClientWindowsValidation';
import FpChipRadio from '@/components/inputs/FpChipRadio.vue';
import {Utils} from '@/services/utils/BasicUtils';
import {UserLocalStorage} from '@/services/UserLocalStorage';
import UserBadge from '@/components/badges/UserBadge.vue';
import getDevelopmentVerificationCode from '@/commons/services/getDevelopmentVerificationCode';
import {I18NGetter} from '@/services/enumTranslator/I18NGetter';
import {I18NInterface} from '@/services/enumTranslator/I18NInterface';
import { DealStage } from '@/modules/deals/enums/DealStage';

@Component({
  components: {UserBadge, FpChipRadio, CreateClientWindowsForm, AddClientScans, NavigationTabs, EditClientForm, },
})
export default class CreateClient extends VueModalComponent<ModalPayload<Client>> {
  public client: Client = new Client();
  public hideClientTypeTabs: boolean = false;
  public scanAccessEndDate: string = this.modalData?.scanAccessEndDate || '';
  public dealStage: DealStage | null = this.modalData?.dealStage || null;
  public step: CreateClientFormStep = CreateClientFormStep.FORM;
  public I18NGetter: () => I18NInterface = I18NGetter;

  public readonly steps: typeof CreateClientFormStep = CreateClientFormStep;
  public readonly CREATE_CLIENTS_WINDOW_REF: string = 'CREATE_CLIENTS_WINDOW_REF';
  public readonly LAST_CLIENT_TYPE: string = 'LAST_CLIENT_TYPE';
  public readonly tabsHeaders: IMultiformIconDictionaryElement<boolean>[] = [
    {
      name_pl: I18NGetter().useCreateClient.TAB_HEADER_INDIVIDUAL,
      type: false,
      icon: 'person',
    },
    {
      name_pl: I18NGetter().useCreateClient.TAB_HEADER_BUSINESS,
      type: true,
      icon: 'business',
    },
  ].filter(item => (
    this.modalData.isCompany !== undefined ? item.type === this.modalData.isCompany : true
  ));

  public mounted(): void {
    if (this.modalData.isClientFromBik || this.$user.isAppDomain.NESTO_RO) {
      this.client.isCompany = false;
      this.hideClientTypeTabs = true;
    } else if (this.modalData.isCompany) {
      this.client.isCompany = true;
    } else {
      const lastClientType = Utils.parseJson<boolean>(UserLocalStorage.getItem(this.LAST_CLIENT_TYPE));
      if (lastClientType !== null) {
        this.client.isCompany = lastClientType;
      }
    }

    if (this.dealStage === DealStage.WON) {
      this.client.verificationType = AgreementType.SCAN;
    }
  }

  get createClientWindowsRef(): ICreateClientWindowsValidation {
    return this.$refs[this.CREATE_CLIENTS_WINDOW_REF] as any as ICreateClientWindowsValidation;
  };

  get isSMS(): boolean {
    return this.client.verificationType === AgreementType.SMS;
  };

  get addClientButtonDisabled(): boolean {
    return this.isSMS
      ? !this.client.smsCode || this.client.smsCode.length < 6
      : !this.isScansFilled;
  }

  get isScansFilled(): boolean {
    return this.client.clientDocuments.every(x => x.file);
  }

  public async onFormSubmit(): Promise<void> {
    if (this.createClientWindowsRef.validateClientForm()) {
      try {
        if (this.isSMS) {
          await AddClientSmsApi.sendVerificationCode(this.client.phone!);
          this.$snackbarService.openSuccessSnackbar(I18NGetter().useCreateClient.OPEN_SUCCESS_SNACKBAR_SMS);
        }

        if (this.$env.isPreprodOrDev) {
          this.client.smsCode = await getDevelopmentVerificationCode(this.client.phone!);
        }

        this.step = CreateClientFormStep.VERIFICATION;
      } catch (e) {
        this.errorHandler(e, I18NGetter().useCreateClient.ERROR_HANDLER_SMS);
      }
    }
  }

  public async addClient() {
    if (this.createClientWindowsRef.validateClientVerificationForm()) {
      try {
        let response: Client;
        this.client.name = this.client.displayName;
        if (this.isSMS) {
          response = await ClientApi.addClient(this.client);
        } else {
          const clientToSend = this.client.isCompany ? this.client.companyObject : this.client.clientObject;
          const dataToSend = (this.dealStage === DealStage.WON || this.scanAccessEndDate) && this.modalData?.dealId
            ? {dealId: this.modalData.dealId, ...clientToSend,}
            : clientToSend;
          const formToSend = new FormData();
          formToSend.append('formData', JSON.stringify(dataToSend));
          this.client.clientDocuments.forEach(scan => formToSend.append(scan.key, scan.file!));
          response = await ClientApi.addClientByScans(formToSend);
        }
        this.closeModal({
          payload: response,
          action: ModalAction.ADD,
        });
      } catch (e) {
        this.errorHandler(e, I18NGetter().useCreateClient.ERROR_HANDLER_ADDING_CLIENT);
      }
    }
  }

  onExistingClient(clientId: string) {
    this.$goto.client(Number(clientId));
    this.closeModal();
  }
}
</script>
